import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { integrationDetails, integrations } from './marketplaceData';

const IntegrationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const integration = integrations.find((i) => i.id === id);
  const details = integrationDetails[id || ''];

  if (!integration) {
    return (
      <div className="p-6 text-white">
        <p>Integration not found</p>
        <button onClick={() => navigate('/marketplace')} className="text-[#D12929] hover:underline mt-4">
          ← Back to Catalog
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <button onClick={() => navigate('/marketplace')} className="hover:text-white">
          Catalog
        </button>
        <span>›</span>
        <span className="text-white">{integration.name}</span>
      </div>

      {/* Header */}
      <div className="bg-[#1f1f1f] border border-[#474646] rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-16 h-16 bg-[#2e2e2f] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {integration.logoUrl ? (
              <img
                src={integration.logoUrl}
                alt={integration.name}
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className={`text-white font-bold text-xl ${integration.logoUrl ? 'hidden' : ''}`}>{integration.icon}</span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-white text-2xl font-bold">{integration.name}</h1>
              <span className="px-3 py-1 bg-[#D12929] text-white text-sm rounded">{integration.category}</span>
              {integration.partnerHosted && (
                <span className="px-3 py-1 bg-[#2e2e2f] text-gray-300 text-xs rounded border border-[#474646]">Partner Hosted</span>
              )}
            </div>
            <p className="text-gray-400 mb-4">{integration.vendor}</p>
            <p className="text-gray-300">{integration.description}</p>
          </div>
        </div>
      </div>

      {/* Available Integrations */}
      <div className="mb-6">
        <h2 className="text-white text-xl font-semibold mb-4">Available Integrations</h2>

        {details?.availableIntegrations ? (
          <div className="space-y-3">
            {details.availableIntegrations.map((availIntegration) => (
              <div
                key={availIntegration.id}
                className="bg-[#1f1f1f] border border-[#474646] rounded-lg p-4 hover:border-[#D12929] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-white font-semibold">{availIntegration.name}</h3>
                      <span className="px-2 py-1 bg-[#D12929] text-white text-xs rounded">{availIntegration.type}</span>
                    </div>
                    <p className="text-gray-300 text-sm mb-3">{availIntegration.description}</p>

                    {/* Note */}
                    <div className="bg-[#2e2e2f] border border-[#474646] rounded p-3 mb-3">
                      <p className="text-gray-300 text-sm">
                        <strong>Note:</strong>{' '}
                        {availIntegration.note ||
                          'This integration is partner-built and hosted by the vendor. For additional information see links below and contact the vendor.'}
                      </p>
                    </div>

                    {/* Installation Links */}
                    {availIntegration.installationLinks && availIntegration.installationLinks.length > 0 && (
                      <div className="mb-3">
                        <p className="text-gray-300 text-sm mb-2">Installation Links</p>
                        <div className="space-y-2">
                          {availIntegration.installationLinks.map((link) => (
                            <a
                              key={link.url}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:underline text-sm flex items-center gap-1"
                            >
                              {link.label} <ExternalLink size={12} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Links */}
                  <div className="ml-6 bg-[#2e2e2f] border border-[#474646] rounded p-4 w-64 flex-shrink-0">
                    <h4 className="text-white font-semibold mb-3">Quick Links</h4>
                    {availIntegration.quickLinks && availIntegration.quickLinks.length > 0 ? (
                      <div className="space-y-2">
                        {availIntegration.quickLinks.map((link) => (
                          <a
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:underline text-sm flex items-center gap-1"
                          >
                            {link.label} <ExternalLink size={12} />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No quick links available.</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#1f1f1f] border border-[#474646] rounded-lg p-6 text-center">
            <p className="text-gray-400">No integrations available for this product yet.</p>
            <p className="text-gray-500 text-sm mt-2">Check back later or contact support for more information.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationDetail;
